/**
 * @file This file exports the EditorToolbar component, which is a toolbar for the tiptap editor.
 * It provides various formatting options such as bold, italic, strike, code, clear formatting, plain text, headings, and text alignment.
 * It also provides table editing options such as adding/deleting rows/columns, merging/splitting cells, and toggling header cells/rows/columns.
 */

import { DevTool } from '@hookform/devtools';
import { zodResolver } from '@hookform/resolvers/zod';
import { TextField } from '@livekatsomo/web/ui-components/react-hook-form-components';
import AlignHorizontalCenter from '@mui/icons-material/AlignHorizontalCenter';
import AlignHorizontalLeft from '@mui/icons-material/AlignHorizontalLeft';
import AlignHorizontalRight from '@mui/icons-material/AlignHorizontalRight';
import Code from '@mui/icons-material/Code';
import FormatAlignJustify from '@mui/icons-material/FormatAlignJustify';
import FormatBold from '@mui/icons-material/FormatBold';
import FormatClear from '@mui/icons-material/FormatClear';
import FormatItalic from '@mui/icons-material/FormatItalic';
import FormatListBulleted from '@mui/icons-material/FormatListBulleted';
import FormatListNumbered from '@mui/icons-material/FormatListNumbered';
import FormatQuote from '@mui/icons-material/FormatQuote';
import FormatStrikethrough from '@mui/icons-material/FormatStrikethrough';
import HorizontalRule from '@mui/icons-material/HorizontalRule';
import ImageIcon from '@mui/icons-material/Image';
import KeyboardReturn from '@mui/icons-material/KeyboardReturn';
import LayersClear from '@mui/icons-material/LayersClear';
import Link from '@mui/icons-material/Link';
import Redo from '@mui/icons-material/Redo';
import Undo from '@mui/icons-material/Undo';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Tooltip from '@mui/material/Tooltip';
import { Editor } from '@tiptap/react';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { HeadingButton } from '../HeadingButton/HeadingButton';
import { AddColumnAfter } from './icons/AddColumnAfter';
import { AddColumnBefore } from './icons/AddColumnBefore';
import { AddRowAfter } from './icons/AddRowAfter';
import { AddRowBefore } from './icons/AddRowBefore';
import { DeleteColumn } from './icons/DeleteColumn';
import { DeleteRow } from './icons/DeleteRow';
import { DeleteTable } from './icons/DeleteTable';
import { FixTables } from './icons/FixTables';
import { InsertTable } from './icons/InsertTable';
import { MergeCells } from './icons/MergeCells';
import { NextCell } from './icons/NextCell';
import { PreviousCell } from './icons/PreviousCell';
import { SplitCell } from './icons/SplitCell';
import { ToggleHeaderCell } from './icons/ToggleHeaderCell';
import { ToggleHeaderColumn } from './icons/ToggleHeaderColumn';
import { ToggleHeaderRow } from './icons/ToggleHeaderRow';

/**
 * Props for the EditorToolbar component.
 * @interface
 * @property {Editor | null} editor - The tiptap editor instance.
 * @property {() => void} onAddImage - A function to add an image to the editor.
 */
export interface EditorToolbarProps {
  editor: Editor | null;
  onAddImage?: () => void;
}

/**
 * The EditorToolbar component is a toolbar for the tiptap editor.
 * It provides various formatting options such as bold, italic, strike, code, clear formatting, plain text, headings, and text alignment.
 * It also provides table editing options such as adding/deleting rows/columns, merging/splitting cells, and toggling header cells/rows/columns.
 * @param props - The props for the EditorToolbar component.
 * @returns A JSX Element representing the EditorToolbar component.
 */
export function EditorToolbar({ editor, onAddImage }: EditorToolbarProps) {
  const { t } = useTranslation();
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);

  if (!editor) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      <ButtonGroup variant="contained">
        <Tooltip title={t('Bold')}>
          <Button
            onClick={() => editor.chain().focus().toggleBold().run()}
            color={editor.isActive('bold') ? 'secondary' : 'primary'}
          >
            <FormatBold />
          </Button>
        </Tooltip>
        <Tooltip title={t('Italic')}>
          <Button
            onClick={() => editor.chain().focus().toggleItalic().run()}
            color={editor.isActive('italic') ? 'secondary' : 'primary'}
          >
            <FormatItalic />
          </Button>
        </Tooltip>
        <Tooltip title={t('Strike')}>
          <Button
            onClick={() => editor.chain().focus().toggleStrike().run()}
            color={editor.isActive('strike') ? 'secondary' : 'primary'}
          >
            <FormatStrikethrough />
          </Button>
        </Tooltip>
        <Tooltip title={t('Code')}>
          <Button
            onClick={() => editor.chain().focus().toggleCode().run()}
            color={editor.isActive('code') ? 'secondary' : 'primary'}
          >
            <Code />
          </Button>
        </Tooltip>
        <Tooltip title={t('Link')}>
          <Button
            onClick={() => setLinkDialogOpen(true)}
            color={editor.isActive('link') ? 'secondary' : 'primary'}
          >
            <Link />
          </Button>
        </Tooltip>
      </ButtonGroup>
      <ButtonGroup variant="contained">
        <Tooltip title={t('Clear formatting')}>
          <Button onClick={() => editor.chain().focus().unsetAllMarks().run()}>
            <FormatClear />
          </Button>
        </Tooltip>
        <Tooltip title={t('Plain text')}>
          <Button onClick={() => editor.chain().focus().clearNodes().run()}>
            <LayersClear />
          </Button>
        </Tooltip>
      </ButtonGroup>
      <HeadingButton editor={editor} />
      <ButtonGroup variant="contained">
        <Tooltip title={t('Align Left')}>
          <Button
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            color={
              editor.isActive('textAlign', { textAlign: 'left' })
                ? 'secondary'
                : 'primary'
            }
          >
            <AlignHorizontalLeft />
          </Button>
        </Tooltip>
        <Tooltip title={t('Align Middle')}>
          <Button
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            color={
              editor.isActive('textAlign', { textAlign: 'center' })
                ? 'secondary'
                : 'primary'
            }
          >
            <AlignHorizontalCenter />
          </Button>
        </Tooltip>
        <Tooltip title={t('Align Right')}>
          <Button
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            color={
              editor.isActive('textAlign', { textAlign: 'right' })
                ? 'secondary'
                : 'primary'
            }
          >
            <AlignHorizontalRight />
          </Button>
        </Tooltip>
        <Tooltip title={t('Justify')}>
          <Button
            onClick={() => editor.chain().focus().setTextAlign('justify').run()}
            color={
              editor.isActive('textAlign', { textAlign: 'justify' })
                ? 'secondary'
                : 'primary'
            }
          >
            <FormatAlignJustify />
          </Button>
        </Tooltip>
      </ButtonGroup>
      <ButtonGroup variant="contained">
        <Tooltip title={t('Bulleted list')}>
          <Button
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            color={editor.isActive('bulletList') ? 'secondary' : 'primary'}
          >
            <FormatListBulleted />
          </Button>
        </Tooltip>
        <Tooltip title={t('Numbered list')}>
          <Button
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            color={editor.isActive('orderedList') ? 'secondary' : 'primary'}
          >
            <FormatListNumbered />
          </Button>
        </Tooltip>
      </ButtonGroup>
      <ButtonGroup variant="contained">
        <Tooltip title={t('Code Block')}>
          <Button
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            color={editor.isActive('codeBlock') ? 'secondary' : 'primary'}
          >
            <Code />
          </Button>
        </Tooltip>
        <Tooltip title={t('Quotation')}>
          <Button
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            color={editor.isActive('blockquote') ? 'secondary' : 'primary'}
          >
            <FormatQuote />
          </Button>
        </Tooltip>
      </ButtonGroup>
      <ButtonGroup variant="contained">
        <Tooltip title={t('Horizontal Line')}>
          <Button
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
          >
            <HorizontalRule />
          </Button>
        </Tooltip>
        <Tooltip title={t('Line break')}>
          <Button onClick={() => editor.chain().focus().setHardBreak().run()}>
            <KeyboardReturn />
          </Button>
        </Tooltip>
      </ButtonGroup>
      <ButtonGroup variant="contained">
        <Tooltip title={t('Undo')}>
          <Button onClick={() => editor.chain().focus().undo().run()}>
            <Undo />
          </Button>
        </Tooltip>
        <Tooltip title={t('Redo')}>
          <Button onClick={() => editor.chain().focus().redo().run()}>
            <Redo />
          </Button>
        </Tooltip>
      </ButtonGroup>
      <ButtonGroup variant="contained">
        <Tooltip title={t('Insert table')}>
          <Button
            onClick={() =>
              editor
                .chain()
                .focus()
                .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                .run()
            }
          >
            <InsertTable />
          </Button>
        </Tooltip>
        <Tooltip title={t('Delete Table')}>
          <Button onClick={() => editor.chain().focus().deleteTable().run()}>
            <DeleteTable />
          </Button>
        </Tooltip>
        <Tooltip title={t('Fix Tables')}>
          <Button onClick={() => editor.chain().focus().fixTables().run()}>
            <FixTables />
          </Button>
        </Tooltip>
      </ButtonGroup>
      <ButtonGroup variant="contained">
        <Tooltip title={t('Add Column Before')}>
          <Button
            onClick={() => editor.chain().focus().addColumnBefore().run()}
          >
            <AddColumnBefore />
          </Button>
        </Tooltip>
        <Tooltip title={t('Add Column After')}>
          <Button onClick={() => editor.chain().focus().addColumnAfter().run()}>
            <AddColumnAfter />
          </Button>
        </Tooltip>
        <Tooltip title={t('Delete Column')}>
          <Button onClick={() => editor.chain().focus().deleteColumn().run()}>
            <DeleteColumn />
          </Button>
        </Tooltip>
        <Tooltip title={t('Add Row Before')}>
          <Button onClick={() => editor.chain().focus().addRowBefore().run()}>
            <AddRowBefore />
          </Button>
        </Tooltip>
        <Tooltip title={t('Add Row After')}>
          <Button onClick={() => editor.chain().focus().addRowAfter().run()}>
            <AddRowAfter />
          </Button>
        </Tooltip>
        <Tooltip title={t('Delete Row')}>
          <Button onClick={() => editor.chain().focus().deleteRow().run()}>
            <DeleteRow />
          </Button>
        </Tooltip>
        <Tooltip title={t('Merge Cells')}>
          <Button onClick={() => editor.chain().focus().mergeCells().run()}>
            <MergeCells />
          </Button>
        </Tooltip>
        <Tooltip title={t('Split Cell')}>
          <Button onClick={() => editor.chain().focus().splitCell().run()}>
            <SplitCell />
          </Button>
        </Tooltip>
      </ButtonGroup>
      <ButtonGroup variant="contained">
        <Tooltip title={t('Toggle Header Column')}>
          <Button
            onClick={() => editor.chain().focus().toggleHeaderColumn().run()}
          >
            <ToggleHeaderColumn />
          </Button>
        </Tooltip>
        <Tooltip title={t('Toggle Header Row')}>
          <Button
            onClick={() => editor.chain().focus().toggleHeaderRow().run()}
          >
            <ToggleHeaderRow />
          </Button>
        </Tooltip>
        <Tooltip title={t('Toggle Header Cell')}>
          <Button
            onClick={() => editor.chain().focus().toggleHeaderCell().run()}
          >
            <ToggleHeaderCell />
          </Button>
        </Tooltip>
        <Tooltip title={t('Previous Cell')}>
          <Button
            onClick={() => editor.chain().focus().goToPreviousCell().run()}
          >
            <PreviousCell />
          </Button>
        </Tooltip>
        <Tooltip title={t('Next Cell')}>
          <Button onClick={() => editor.chain().focus().goToNextCell().run()}>
            <NextCell />
          </Button>
        </Tooltip>
      </ButtonGroup>
      {onAddImage ? (
        <ButtonGroup variant="contained">
          <Tooltip title={t('Add image')}>
            <Button onClick={onAddImage}>
              <ImageIcon />
            </Button>
          </Tooltip>
        </ButtonGroup>
      ) : null}
      <LinkDialog
        editor={editor}
        isOpen={linkDialogOpen}
        onClose={() => setLinkDialogOpen(false)}
      />
    </Box>
  );
}

export default EditorToolbar;

interface LinkDialogProps extends LinkFormProps {
  isOpen: boolean;
}

function LinkDialog({ isOpen, onClose, ...props }: LinkDialogProps) {
  const { t } = useTranslation();
  const url = props.editor.getAttributes('link').href;
  return (
    <Dialog scroll="body" open={isOpen} onClose={onClose}>
      <Box sx={{ position: 'relative' }}>
        <DialogTitle>{url ? t('Edit Link') : t('Add Link')}</DialogTitle>
        <DialogContent>
          <LinkForm url={url} onClose={onClose} {...props} />
        </DialogContent>
      </Box>
    </Dialog>
  );
}

const linkSchema = z.object({
  url: z.string().url(),
});

interface LinkFormProps {
  url?: string;
  onClose: () => void;
  editor: Editor;
}

function LinkForm({ url, editor, onClose }: LinkFormProps) {
  const { t } = useTranslation();
  const methods = useForm({
    defaultValues: {
      url: url || '',
    },
    resolver: zodResolver(linkSchema),
  });
  const {
    formState: { isValid, isDirty },
  } = methods;

  const submit = (values: { url: string }) => {
    console.log('link form submitted', values);
    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: values.url })
      .run();
    onClose();
  };

  return (
    <FormProvider {...methods}>
      <DevTool control={methods.control} />
      <form onSubmit={methods.handleSubmit(submit)}>
        <TextField
          autoFocus
          margin="dense"
          name="url"
          label={t('url')}
          type="text"
          fullWidth
          variant="standard"
        />
      </form>
      <DialogActions>
        <Button onClick={onClose}>{t('Cancel')}</Button>
        <Button
          type="button"
          disabled={!isDirty}
          onClick={() => methods.reset()}
        >
          {t('Reset')}
        </Button>
        <Button
          onClick={methods.handleSubmit(submit)}
          disabled={!isValid || !isDirty}
        >
          {url ? t('Update') : t('Add')}
        </Button>
        {editor.isActive('link') && (
          <Button
            onClick={() => {
              editor.chain().focus().extendMarkRange('link').unsetLink().run();
              onClose();
            }}
          >
            {t('Remove')}
          </Button>
        )}
      </DialogActions>
    </FormProvider>
  );
}
